import { useState, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Row, Checkbox } from "@narmi/design_system";
import { Dialog, catcat as cc } from "cerulean";
import { type ReversalReason } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useFetchPaymentMetadataById } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  ReversalReasonSelect,
  ReversalDetailRow,
  ReversalReceipt,
  ReversalReceiptFooter,
  useReversalReceiptTitle,
  type ReversalReceiptRow,
} from "../common";
import styles from "../common/reversalShared.module.scss";
import type { PaymentMetadataProps } from "./PaymentActivityDrawer";

export type ReversiblePaymentType = {
  payment: API.FormattedPaymentActivity;
  metadata?: PaymentMetadataProps | null;
};

export type ReversePaymentModalProps = {
  isOpen: boolean;
  reversiblePayment: ReversiblePaymentType | null;
  showReceipt: boolean;
  handleClose: () => void;
  handleReversePayment: (
    reason: ReversalReason,
    cancelRecurring: boolean
  ) => void;
  submitting?: boolean;
};

const ReversePaymentModal = ({
  isOpen,
  reversiblePayment,
  showReceipt,
  handleClose,
  handleReversePayment,
  submitting = false,
}: ReversePaymentModalProps) => {
  const { l10n } = useLocalization();
  const [reason, setReason] = useState<ReversalReason | "">("");
  const [displayError, setDisplayError] = useState(false);
  const [cancelRecurring, setCancelRecurring] = useState(false);
  const [fetchedMetadata, setFetchedMetadata] =
    useState<PaymentMetadataProps | null>(null);
  const receiptTitle = useReversalReceiptTitle();
  const { send: fetchPaymentMetadata } = useFetchPaymentMetadataById();

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setDisplayError(false);
      setCancelRecurring(false);
      setFetchedMetadata(null);
      return;
    }

    // receipt screen does not use payment metadata, so skip the fetch when
    // the modal reopens on the receipt step (ie, returning from sudo)
    if (showReceipt) return;

    // if metadata was passed in (i.e, from the drawer), skip the fetch
    if (reversiblePayment?.metadata) return;
    if (reversiblePayment?.payment.uuid && reversiblePayment?.payment.model) {
      fetchPaymentMetadata(
        reversiblePayment.payment.uuid,
        reversiblePayment.payment.model
      )
        .then((data) => setFetchedMetadata(data ?? null))
        .catch(() => setFetchedMetadata(null));
    }
  }, [
    isOpen,
    showReceipt,
    reversiblePayment?.payment.uuid,
    reversiblePayment?.payment.model,
    reversiblePayment?.metadata,
    fetchPaymentMetadata,
  ]);

  if (!reversiblePayment) return null;

  const { payment } = reversiblePayment;
  const paymentMetadata = reversiblePayment.metadata ?? fetchedMetadata;

  const {
    displayAmountDollars,
    institutionAccount,
    recipient,
    destinationDetails,
    isCollectPayment,
    dualApprovalId,
  } = payment;

  const {
    std_ent_cls_code: stdEntClsCode,
    company_name: companyName,
    company_id: companyId,
    entry_desc: entryDesc,
  } = paymentMetadata ?? {};

  const {
    account_number: accountNumber,
    routing_number: routingNumber,
    institution_name: destinationBankName,
  } = destinationDetails ?? {};

  const bankName =
    destinationBankName || recipient?.ach_destination?.institution_name;

  const companyNameId =
    companyName && companyId
      ? `${companyName} / ${companyId}`
      : companyName ?? companyId;

  const hasRecurringPayments =
    Boolean(payment.rruleModel) && Boolean(payment.parent);

  const handleSubmit = () => {
    if (!reason) {
      setDisplayError(true);
      return;
    }
    handleReversePayment(reason, cancelRecurring);
  };

  const handleReasonChange = (value: ReversalReason) => {
    setReason(value);
    if (displayError) {
      setDisplayError(false);
    }
  };

  const handleCopyPaymentLink = () => {
    // TODO: update when backend returns dual approval ID for that reversal
    if (!dualApprovalId) return;
    const approvalUrl = `${window.location.origin}/approvals/pending?dualApprovalId=${dualApprovalId}`;
    navigator.clipboard.writeText(approvalUrl);
  };

  const recipientLabel = isCollectPayment
    ? l10n.getString("single-ach-payment-recipient-collect-from")
    : l10n.getString("single-ach-payment-recipient-send-to");

  const receiptRows: ReversalReceiptRow[] = [
    { label: recipientLabel, value: recipient?.name },
    { label: l10n.getString("label-amount"), value: displayAmountDollars },
    {
      label: l10n.getString("label-delivery-speed"),
      value: l10n.getString("ach-payment-receipt-delivery"),
    },
  ];

  const dialogTitle = showReceipt
    ? receiptTitle
    : l10n.getString("label-reverse-payment");

  const renderReversalForm = () => (
    <div>
      <p className="fontColor--secondary margin--top--l">
        {l10n.getString("reverse-payment-modal-subtitle")}
      </p>

      <div className={styles.modalSection}>
        <ReversalReasonSelect
          reason={reason}
          onChange={handleReasonChange}
          errorText={
            displayError
              ? l10n.getString("reverse-payment-reason-required")
              : undefined
          }
        />
      </div>

      <div className={cc(styles.modalSection, "margin--top--l")}>
        <p className="fontColor--heading fontWeight--normal fontSize--l margin--bottom--m">
          {l10n.getString("single-ach-payment-review-and-confirm-title")}
        </p>

        <h6 className="fontColor--heading fontWeight--bold fontSize--xs margin--bottom--s">
          {l10n.getString("ach-payment-payment-details-title").toUpperCase()}
        </h6>
        <ReversalDetailRow
          label={l10n.getString("label-amount")}
          value={displayAmountDollars}
        />
        <ReversalDetailRow
          label={l10n.getString("label-account")}
          value={
            institutionAccount
              ? `${
                  institutionAccount.name
                }-${institutionAccount.getMaskedNumber()}`
              : undefined
          }
        />
        <ReversalDetailRow
          label={l10n.getString("label-transaction-type")}
          value={
            stdEntClsCode
              ? l10n.getString("label-std-code-payment", {
                  code: stdEntClsCode,
                })
              : undefined
          }
        />
        <ReversalDetailRow
          label={l10n.getString("label-company-name-id")}
          value={companyNameId}
        />
        <ReversalDetailRow
          label={l10n.getString("ach-payment-entry-desc")}
          value={entryDesc}
        />
      </div>

      <div className={cc(styles.modalSection, "margin--top--l")}>
        <h6 className="fontColor--heading fontWeight--bold fontSize--xs margin--top--l margin--bottom--s">
          {l10n.getString("recipient-details").toUpperCase()}
        </h6>
        <ReversalDetailRow
          label={l10n.getString("label-recipient")}
          value={recipient?.name}
        />
        <ReversalDetailRow
          label={l10n.getString("reverse-payment-detail-bank")}
          value={bankName}
        />
        <ReversalDetailRow
          label={l10n.getString("label-account-number")}
          value={accountNumber}
        />
        <ReversalDetailRow
          label={l10n.getString("label-routing")}
          value={routingNumber}
        />

        {hasRecurringPayments && (
          <div className="margin--top--l">
            <Checkbox
              checked={cancelRecurring}
              onChange={() => setCancelRecurring(!cancelRecurring)}
              label={l10n.getString("reverse-payment-cancel-recurring-label")}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderFooter = () => (
    <Row alignItems="center" justifyContent="end">
      <Row.Item shrink>
        <Button kind="secondary" onClick={handleClose}>
          {l10n.getString("button-back")}
        </Button>
      </Row.Item>
      <Row.Item shrink>
        <Button onClick={handleSubmit} disabled={submitting}>
          {l10n.getString("label-reverse-payment")}
        </Button>
      </Row.Item>
    </Row>
  );

  return (
    <Dialog
      width="595px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      title={dialogTitle}
      footer={
        showReceipt ? (
          <ReversalReceiptFooter
            handleClose={handleClose}
            handleCopyPaymentLink={handleCopyPaymentLink}
          />
        ) : (
          renderFooter()
        )
      }
    >
      {showReceipt ? (
        <div className={styles.modalSection}>
          <ReversalReceipt rows={receiptRows} />
        </div>
      ) : (
        renderReversalForm()
      )}
    </Dialog>
  );
};

export default ReversePaymentModal;
